import { DayEntry, Timestamp } from '@griply/types';
import { isSameDay } from 'date-fns';

export enum SlotVariant {
  /** Entry row — checkbox (today/past) or calendar icon (future explicit). */
  Entry = 'entry',
  /** Empty tappable row for a past/today day with no entry. */
  Add = 'add',
  /** "Repeat until completed" row — repeat icon + frequency label. */
  UntilCompleted = 'until-completed',
}

/**
 * Decides which slot rows a single day within an UpcomingPeriod renders.
 *
 * Mirrors web's UpcomingSlot two-branch JSX so mobile and web stay in sync:
 *   1. Entry/Add row renders when the entry is not a generated frequency entry,
 *      OR the date is today, OR the entry already has a state (complete/skip/fail).
 *   2. UntilCompleted row renders when the entry is a generated frequency entry
 *      with no state yet (future "repeat until completed" placeholder).
 *
 * Both can fire for the same date — e.g. today with a generated-frequency slot
 * renders the Entry row (tappable checkbox) and can still show the UntilCompleted
 * reminder if relevant.
 */
export const getSlotVariants = (
  entry: DayEntry | undefined,
  date: Timestamp,
  today: Timestamp,
): SlotVariant[] => {
  const isToday = isSameDay(date, today);
  const hasEntryState = !!entry?.state;
  const variants: SlotVariant[] = [];

  if (!entry?.isGeneratedFrequencyEntry || isToday || hasEntryState) {
    variants.push(entry ? SlotVariant.Entry : SlotVariant.Add);
  }

  if (entry?.isGeneratedFrequencyEntry && !hasEntryState) {
    variants.push(SlotVariant.UntilCompleted);
  }

  return variants;
};
